import React, { useState, useContext, FC, useMemo, Suspense } from 'react';
import cx from 'classnames';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import loadable from '@loadable/component';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import NavLogo from 'components/navV2/NavLogo';
import NavLoginOrSignUp from 'components/navV2/NavLoginOrSignUp';
import BoardHeart from 'components/nav/TopNav/BoardHeart';
import CartContainer from 'components/nav/TopNav/CartContainer';
import Notification from 'components/nav/TopNav/Notification';
import NavContext from 'components/navV2/NavScrollContainer/context';
import { isLoggedIn } from 'util/authHelpers';
import { UniversalTabs } from 'reducers/nav';
import RebrandLogoWhite from '@zola/zola-ui/src/assets/images/rebrand/zola-logomark-cloud.svg';
import Messages from 'components/nav/TopNav/Messages';
import { getFavoritesLink } from 'util/getFavoritesLink';
import { connect } from 'react-redux';
import { getUserContext } from 'selectors/user';
import { RootState } from '../../../reducers';

import 'components/navV2/NavUtil/navUtil.less';

export const SearchContainer = loadable(
  () => import(/* webpackChunkName: "search-container" */ 'components/nav/Search/SearchContainer'),
  { ssr: false }
);

type NavUtilProps = {
  userContext: UserContext;
  activeUniversalTab: string;
  isHomeStoreActive: boolean;
};

// TODO: This component is being rendered at least 5 times on a single page load. Need to investigate
export const NavUtilContent: FC<NavUtilProps> = ({
  userContext,
  activeUniversalTab,
  isHomeStoreActive,
}) => {
  const [isSearchActive, setSearchActive] = useState(false);

  const toggleSearchActive = () => {
    setSearchActive(!isSearchActive);
  };

  const { hidePrimarySection, activeParent, activeLinkId } = useContext(NavContext);
  const activeParentClasses = cx('nav-util__activeParent', {
    'nav-util__activeParent--show': hidePrimarySection,
  });

  const isWeddingStoreActive = activeUniversalTab === UniversalTabs.WEDDINGS;

  const favoritesLink = useMemo(() => getFavoritesLink(activeLinkId), [activeLinkId]);

  const userId = userContext?.object_id as string;
  const hasMarketplaceAccount = userContext?.has_marketplace_account;

  const showEnvelopeIcon = isLoggedIn(userContext) && hasMarketplaceAccount;

  return (
    <div className="nav-util">
      <NavLogo logo={isWeddingStoreActive ? undefined : RebrandLogoWhite} />
      {!isLoggedIn(userContext) ? <div className={activeParentClasses}>{activeParent}</div> : null}
      <Suspense fallback={<div>Loading</div>}>
        <div className={cx('nav-util--right')}>
          <SearchContainer
            isActive={isSearchActive}
            toggleSearchActive={toggleSearchActive}
            hideToggle={!isWeddingStoreActive}
            isWeddingStoreActive={isWeddingStoreActive}
          />
          {isWeddingStoreActive && <BoardHeart userContext={userContext} link={favoritesLink} />}
          {isWeddingStoreActive && !isLoggedIn(userContext) && <NavLoginOrSignUp />}
          {!isHomeStoreActive && showEnvelopeIcon && <Messages userId={userId} />}
          {!isHomeStoreActive && isLoggedIn(userContext) ? <Notification userId={userId} /> : null}
          <CartContainer isNavV2 />
          {isHomeStoreActive && !isLoggedIn(userContext) && (
            <LinkV2 variant="tertiary" noTextTransform href="/account/login">
              Log in
            </LinkV2>
          )}
          {isHomeStoreActive && isLoggedIn(userContext) && (
            <LinkV2 variant="tertiary" noTextTransform href="/account/settings">
              Your account
            </LinkV2>
          )}
        </div>
      </Suspense>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  userContext: getUserContext(state),
});

export default connect(mapStateToProps)(NavUtilContent);
